import { createSelector } from 'reselect';
import groupBy from 'lodash/groupBy';
import memoize from 'lodash/memoize';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { CardListId } from '../../../model/types.tsx';
import type { State } from '../../types.tsx';
import { makeOrderedVisibleCardsForCardList } from '../cards/index.tsx';

type WorkLoadByAssignee = {
	name?: string;
	profilePicture?: string;
	storyPoints: number;
	remainingTimeEstimateInSeconds: number;
	originalTimeEstimateInSeconds: number;
	issueCount: number;
	subtaskCount?: number;
};

type GetWorkLoadByAssigneeFuncType = (cardListId: CardListId) => WorkLoadByAssignee[];

export const getIncludeSubtasks = (state: State) => state.ui.workloadModal.includeSubtasks;

export const makeWorkLoadByAssignee = createSelector(
	[makeOrderedVisibleCardsForCardList, getIncludeSubtasks],
	(getOrderedVisibleCardsForCardList, includeSubtasks) => {
		const getWorkLoadByAssignee: GetWorkLoadByAssigneeFuncType = (cardListId: CardListId) => {
			let listOfVisibleCards = getOrderedVisibleCardsForCardList(cardListId);

			if (!includeSubtasks) {
				listOfVisibleCards = listOfVisibleCards.filter(
					(card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel),
				);
			}

			const cardsGroupedByAssigned = groupBy(
				listOfVisibleCards,
				(card) => card.assignee?.id ?? 'unassigned',
			);

			const assignees = Object.values(cardsGroupedByAssigned).map((cardList) => {
				const initial: WorkLoadByAssignee = {
					name: undefined,
					profilePicture: undefined,
					storyPoints: 0,
					remainingTimeEstimateInSeconds: 0,
					originalTimeEstimateInSeconds: 0,
					issueCount: 0,
					subtaskCount: 0,
				};

				return cardList.reduce((prev: WorkLoadByAssignee, card) => {
					const storyPointIncrement = card.estimate?.storyPoints ?? 0;
					const timeEstimateIncrement = card.estimate?.originalEstimate?.value ?? 0;
					const timeRemainingIncrement = card.estimate?.timeRemainingEstimate?.value ?? 0;

					return {
						name: card.assignee?.displayName ?? prev.name,
						profilePicture: card.assignee?.avatarUrl ?? prev.name,
						storyPoints: prev.storyPoints + storyPointIncrement,
						originalTimeEstimateInSeconds:
							prev.originalTimeEstimateInSeconds + timeEstimateIncrement,
						remainingTimeEstimateInSeconds:
							prev.remainingTimeEstimateInSeconds + timeRemainingIncrement,
						issueCount: isSubtaskHierarchyLevel(card.typeHierarchyLevel)
							? prev.issueCount
							: prev.issueCount + 1,
						subtaskCount: isSubtaskHierarchyLevel(card.typeHierarchyLevel)
							? (prev.subtaskCount ?? 0) + 1
							: prev.subtaskCount,
					};
				}, initial);
			});

			return assignees;
		};

		return memoize(getWorkLoadByAssignee);
	},
);
